import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useLayoutEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import {
  activeCollectionViewFilterCount,
  applyCollectionView,
  extractQuickCaptureWebLink,
  groupCollectionItems as groupItemsByView,
  normalizeCollectionQuery,
  type CollectionViewGroup,
  type CollectionViewLayout,
  type CollectionViewSort,
} from "@note-app/application";
import {
  Button,
  CaptureModeSelector,
  ConfirmDialog,
  Dialog,
  EmptyState,
  ErrorState,
  LoadingState,
  Menu,
  QuickCapture,
  TextField,
  ThemeProvider,
  ToastProvider,
  TypedConfirmDialog,
  type QuickCaptureValue,
  useToast,
} from "@note-app/ui";
import type { KnowledgeItem } from "@note-app/contracts";
import type {
  PlatformRuntime,
  StorageHealth,
} from "@note-app/platform-runtime";

import {
  formatKnowledgeRoute,
  parseKnowledgeRoute,
  type KnowledgeRoute,
} from "./routes.ts";
import {
  KnowledgeAppViewModel,
  type CatalogSnapshot,
  type KnowledgeCollection,
} from "./view-model.ts";
import { CatalogManager } from "./components/CatalogManager.tsx";
import { DOCUMENT_BLOCK_DRAG_MIME, DOCUMENT_BLOCK_DROP_EVENT, parseDocumentBlockDrag } from "./document-block-transfer-dnd.ts";
import { ItemMetadataDialog } from "./components/ItemMetadataDialog.tsx";
import { KnowledgeItemGrid } from "./components/KnowledgeItemGrid.tsx";
import { RouteErrorBoundary } from "./components/RouteErrorBoundary.tsx";
import { GlobalSearchDialog } from "./components/GlobalSearchDialog.tsx";
import {
  KnowledgeIcon,
  type KnowledgeIconName,
} from "./components/KnowledgeIcon.tsx";
import { BrandMenu, NavigationSection } from "./components/NavigationControls.tsx";
import { KnowledgeRouteLeaveRegistry } from "./route-leave-guard.ts";

const RichDocumentPage = lazy(
  () => import("./components/RichDocumentPage.tsx"),
);
const KnowledgeCanvasPage = lazy(
  () => import("./components/KnowledgeCanvasPage.tsx"),
);

export interface KnowledgeAppNotice {
  readonly id: string;
  readonly tone: "info" | "warning" | "success" | "error";
  readonly title: string;
  readonly description?: string;
  readonly actionLabel?: string;
  readonly onAction?: () => void | Promise<void>;
}

export interface KnowledgeAppProps {
  readonly runtime: PlatformRuntime;
  readonly notices?: readonly KnowledgeAppNotice[];
  readonly initialHash?: string;
  readonly onNavigate?: (hash: string) => void;
}

export function KnowledgeApp(props: KnowledgeAppProps) {
  return (
    <ThemeProvider defaultTheme="system">
      <ToastProvider>
        <KnowledgeAppSurface {...props} />
      </ToastProvider>
    </ThemeProvider>
  );
}

function KnowledgeAppSurface({
  runtime,
  notices = [],
  initialHash,
  onNavigate,
}: KnowledgeAppProps) {
  const { showToast } = useToast();
  const viewModel = useMemo(() => new KnowledgeAppViewModel(runtime), [runtime]);
  const routeLeaveRegistry = useMemo(
    () => new KnowledgeRouteLeaveRegistry(),
    [],
  );
  const [route, setRoute] = useState<KnowledgeRoute>(() =>
    parseKnowledgeRoute(
      initialHash ?? (typeof location === "undefined" ? "#/" : location.hash),
    ),
  );
  const appRoot = useRef<HTMLDivElement>(null);
  const [canvasHeaderTarget, setCanvasHeaderTarget] = useState<HTMLDivElement | null>(null);
  const [mobileNavigationOpen, setMobileNavigationOpen] = useState(false);

  useLayoutEffect(() => {
    const root = appRoot.current;
    const header = root?.querySelector<HTMLElement>(".knowledge-topbar");
    const mobileNav = root?.querySelector<HTMLElement>(".knowledge-mobile-nav");
    if (!root || !header) return;
    const measure = () => {
      const headerHeight = header.getBoundingClientRect().height;
      root.style.setProperty("--knowledge-topbar-height", `${headerHeight}px`);
      root.style.setProperty("--knowledge-sticky-offset", `${headerHeight}px`);
      root.style.setProperty("--knowledge-bottomnav-height", `${mobileNav?.getBoundingClientRect().height ?? 0}px`);
    };
    measure();
    const observer = typeof ResizeObserver === "undefined" ? undefined : new ResizeObserver(measure);
    observer?.observe(header);
    if (mobileNav) observer?.observe(mobileNav);
    return () => observer?.disconnect();
  }, []);

  useEffect(() => {
    if (typeof window.matchMedia !== "function") return;
    const desktop = window.matchMedia("(min-width: 761px)");
    const closeMobile = () => { if (desktop.matches) setMobileNavigationOpen(false); };
    desktop.addEventListener("change", closeMobile);
    return () => desktop.removeEventListener("change", closeMobile);
  }, []);

  const [captureOpen, setCaptureOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [creatingKind, setCreatingKind] = useState<"document" | "canvas">();
  const [statusOpen, setStatusOpen] = useState(false);
  const routineNoticeIds = new Set(["install", "storage-risk", "offline", "offline-ready"]);
  const contentNotices = notices.filter((notice) => !routineNoticeIds.has(notice.id));
  const storageRisk = notices.some((notice) => notice.id === "storage-risk");
  const offline = notices.some((notice) => notice.id === "offline");
  const statusLabel = offline ? "已离线" : storageRisk ? "备份提醒" : "本机存储";
  const committedHash = useRef(
    initialHash ??
      (typeof location === "undefined" ? "#/" : location.hash || "#/"),
  );
  const navigationGeneration = useRef(0);

  useLayoutEffect(() => {
    if (typeof document === "undefined") return;
    document.documentElement.scrollTop = 0;
    document.documentElement.scrollLeft = 0;
    if (document.body) {
      document.body.scrollTop = 0;
      document.body.scrollLeft = 0;
    }
  }, [route]);

  useEffect(() => {
    if (initialHash !== undefined || typeof window === "undefined") return;
    const sync = () => {
      const hash = location.hash || "#/";
      if (hash === committedHash.current) return;
      const generation = ++navigationGeneration.current;
      void routeLeaveRegistry.flush("document-switch").then(
        () => {
          if (generation !== navigationGeneration.current) return;
          committedHash.current = hash;
          setRoute(parseKnowledgeRoute(hash));
        },
        (cause: unknown) => {
          if (generation !== navigationGeneration.current) return;
          history.replaceState(null, "", committedHash.current);
          showToast({
            tone: "error",
            title: "尚未离开当前笔记",
            description: `保存失败：${errorMessage(cause)}`,
          });
        },
      );
    };
    window.addEventListener("hashchange", sync);
    return () => window.removeEventListener("hashchange", sync);
  }, [initialHash, routeLeaveRegistry, showToast]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const flushInBackground = () => {
      void routeLeaveRegistry.flush("window-close").catch(() => undefined);
    };
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (!routeLeaveRegistry.hasPendingChanges()) return;
      flushInBackground();
      event.preventDefault();
      event.returnValue = "";
    };
    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") flushInBackground();
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    window.addEventListener("pagehide", flushInBackground);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      window.removeEventListener("pagehide", flushInBackground);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [routeLeaveRegistry]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const handleGlobalShortcut = (event: KeyboardEvent) => {
      if (!(event.metaKey || event.ctrlKey)) return;
      if (event.key.toLocaleLowerCase() === "k") {
        event.preventDefault();
        setSearchOpen(true);
      } else if (
        event.shiftKey &&
        event.key.toLocaleLowerCase() === "n"
      ) {
        event.preventDefault();
        setCaptureOpen(true);
      }
    };
    window.addEventListener("keydown", handleGlobalShortcut);
    return () => window.removeEventListener("keydown", handleGlobalShortcut);
  }, []);

  const navigate = useCallback(
    (next: KnowledgeRoute) => {
      const hash = formatKnowledgeRoute(next);
      const generation = ++navigationGeneration.current;
      void routeLeaveRegistry.flush("document-switch").then(
        () => {
          if (generation !== navigationGeneration.current) return;
          committedHash.current = hash;
          setRoute(next);
          onNavigate?.(hash);
          if (
            initialHash === undefined &&
            typeof location !== "undefined" &&
            location.hash !== hash
          ) {
            location.hash = hash;
          }
        },
        (cause: unknown) => {
          if (generation !== navigationGeneration.current) return;
          showToast({
            tone: "error",
            title: "尚未离开当前笔记",
            description: `保存失败：${errorMessage(cause)}`,
          });
        },
      );
    },
    [initialHash, onNavigate, routeLeaveRegistry, showToast],
  );

  const createItem = useCallback(
    async (kind: "document" | "canvas") => {
      if (creatingKind) return;
      setCreatingKind(kind);
      try {
        const item =
          kind === "document"
            ? await viewModel.createDocument()
            : await viewModel.createCanvas();
        navigate(
          kind === "document"
            ? { name: "document", itemId: item.id }
            : { name: "canvas", itemId: item.id },
        );
      } catch (cause) {
        showToast({
          tone: "error",
          title: kind === "document" ? "无法新建文档" : "无法新建画板",
          description: errorMessage(cause),
        });
      } finally {
        setCreatingKind(undefined);
      }
    },
    [creatingKind, navigate, showToast, viewModel],
  );

  return (
    <div
      ref={appRoot}
      className={`knowledge-app knowledge-app--${runtime.platform}`}
      data-platform={runtime.platform}
    >
      <a className="knowledge-skip-link" href="#knowledge-main">
        跳到主要内容
      </a>
      <header className="knowledge-topbar">
        <div className="knowledge-masthead">
          <button type="button" className="knowledge-mobile-navigation-trigger"
            aria-label="打开知识库导航" aria-haspopup="dialog" aria-expanded={mobileNavigationOpen}
            onClick={() => setMobileNavigationOpen(true)}><KnowledgeIcon name="menu" /></button>
          <button
            className="knowledge-brand"
            type="button"
            onClick={() => navigate({ name: "home" })}
            aria-label="丰月笔记首页"
          >
            <span className="knowledge-brand__mark" aria-hidden="true">丰</span>
            <span>
              <strong>丰月笔记</strong>
              <small>FengYue Notes</small>
            </span>
          </button>
          <BrandMenu onOpenSettings={() => navigate({ name: "settings" })} />
        </div>
        <button
          className="knowledge-search-trigger"
          type="button"
          onClick={() => setSearchOpen(true)}
        >
          <KnowledgeIcon name="search" />
          <span>搜索笔记、画板或命令</span>
          <kbd>{primaryShortcut("K")}</kbd>
        </button>
        <div className="knowledge-canvas-header-slot" ref={setCanvasHeaderTarget} />
        <div className="knowledge-topbar__actions">
          <button type="button" className="knowledge-status-trigger" data-warning={storageRisk}
            aria-label={`本机状态：${statusLabel}`} title="查看存储、备份与离线状态"
            aria-haspopup="dialog" aria-expanded={statusOpen} onClick={() => setStatusOpen(true)}>
            <KnowledgeIcon name={storageRisk ? "shield" : "check"} />
            <span aria-live="polite">{statusLabel}</span>
          </button>
          <Menu
            label="新建内容"
            className="knowledge-new-menu"
            items={[
              {
                id: "document",
                label: "新建文档",
                icon: <KnowledgeIcon name="document" />,
                shortcut: ["N"],
                disabled: creatingKind !== undefined,
                onSelect: () => void createItem("document"),
              },
              {
                id: "canvas",
                label: "新建画板",
                icon: <KnowledgeIcon name="canvas" />,
                disabled: creatingKind !== undefined,
                onSelect: () => void createItem("canvas"),
              },
              {
                id: "capture",
                label: "快速记录",
                icon: <KnowledgeIcon name="inbox" />,
                shortcut: ["⇧", "N"],
                onSelect: () => setCaptureOpen(true),
              },
            ]}
            trigger={
              <span className="knowledge-new-trigger">
                <KnowledgeIcon name="plus" />
                <span>{creatingKind ? "创建中…" : "新建"}</span>
                <KnowledgeIcon name="chevron-down" />
              </span>
            }
          />
        </div>
      </header>

      <div className={`knowledge-frame${route.name === "canvas" ? " knowledge-frame--canvas" : ""}`}>
        <main id="knowledge-main" className={`knowledge-main${route.name === "document" ? " knowledge-main--document" : route.name === "canvas" ? " knowledge-main--canvas" : ""}`}>
          {contentNotices.length > 0 ? <NoticeStack notices={contentNotices} /> : null}
          <RouteContent
            route={route}
            canvasHeaderTarget={canvasHeaderTarget}
            viewModel={viewModel}
            navigate={navigate}
            routeLeaveRegistry={routeLeaveRegistry}
          />
        </main>
      </div>

      <Dialog open={statusOpen} onOpenChange={setStatusOpen} title="本机与离线状态"
        closeLabel="关闭状态面板" className="knowledge-status-dialog"
        description="查看存储、离线使用和应用更新的状态。"
        footer={<Button onClick={() => { setStatusOpen(false); navigate({ name: "settings" }); }}>打开备份与数据设置</Button>}>
        {notices.length ? <NoticeStack notices={notices} /> : <p>笔记保存在本机，可在数据与设置中导出备份。</p>}
      </Dialog>
      <MobileNavigation route={route} navigate={navigate} />
      <Dialog open={mobileNavigationOpen} onOpenChange={setMobileNavigationOpen}
        title="知识库导航" closeLabel="关闭导航" className="knowledge-navigation-dialog">
        <Navigation route={route} viewModel={viewModel} navigate={(next) => {
          setMobileNavigationOpen(false);
          navigate(next);
        }} />
      </Dialog>
      {captureOpen ? (
        <CaptureSheet
          viewModel={viewModel}
          onClose={() => setCaptureOpen(false)}
          onCreated={(item) => {
            setCaptureOpen(false);
            showToast({
              tone: "success",
              title: "已保存到本机",
              description: item.title,
            });
          }}
        />
      ) : null}
      <GlobalSearchDialog
        open={searchOpen}
        onOpenChange={setSearchOpen}
        viewModel={viewModel}
        navigate={navigate}
        onQuickCapture={() => setCaptureOpen(true)}
        onCreateDocument={() => createItem("document")}
        onCreateCanvas={() => createItem("canvas")}
      />
    </div>
  );
}

function Navigation({
  route,
  navigate,
  viewModel,
}: {
  route: KnowledgeRoute;
  navigate: (route: KnowledgeRoute) => void;
  viewModel: KnowledgeAppViewModel;
}) {
  const catalog = useResource(() => viewModel.loadCatalog(), [viewModel, route]);
  const documents = useResource(() => viewModel.list("document"), [viewModel, route]);
  const favorites = (
          <NavButton active={route.name === "favorites"} label="收藏" onClick={() => navigate({ name: "favorites" })}>
            <KnowledgeIcon name="star" /><span className="knowledge-nav-label">收藏</span>
          </NavButton>
  );
  const sections = (<>
        {documents.status === "ready" && documents.value.length > 0 ? (
          <NavigationSection key={`recent-${route.name}`} label="最近文档" className="knowledge-sidebar__recent-documents">
            {documents.value.slice(0, 6).map((item) => {
              const active = route.name === "document" && route.itemId === item.id;
              return (
                <button
                  key={item.id}
                  type="button"
                  className={active ? "is-active" : ""}
                  aria-current={active ? "page" : undefined}
                  title={`${item.title || "无标题"} · 可将内容块拖到这里`}
                  onClick={() => navigate({ name: "document", itemId: item.id })}
                  onDragOver={(event) => {
                    if (active || !event.dataTransfer.types.includes(DOCUMENT_BLOCK_DRAG_MIME)) return;
                    event.preventDefault();
                    event.dataTransfer.dropEffect = "move";
                    event.currentTarget.classList.add("is-drop-target");
                  }}
                  onDragLeave={(event) => event.currentTarget.classList.remove("is-drop-target")}
                  onDrop={(event) => {
                    event.currentTarget.classList.remove("is-drop-target");
                    if (active) return;
                    event.preventDefault();
                    const payload = parseDocumentBlockDrag(event.dataTransfer);
                    if (!payload) return;
                    window.dispatchEvent(new CustomEvent(DOCUMENT_BLOCK_DROP_EVENT, { detail: { ...payload, targetDocumentId: item.id } }));
                  }}
                >
                  <span aria-hidden="true">{item.icon || "▤"}</span>
                  <span>{item.title || "无标题"}</span>
                </button>
              );
            })}
            <NavButton label="全部最近内容" active={route.name === "recent"} onClick={() => navigate({ name: "recent" })}>
              <KnowledgeIcon name="clock" /><span>全部最近内容</span>
            </NavButton>
          </NavigationSection>
        ) : null}
        {catalog.status === "ready" && (catalog.value.folders.length || catalog.value.tags.length) ? (
          <NavigationSection key={`catalog-${route.name}`} label="文件夹和标签" className="knowledge-sidebar__catalog">
            {catalog.value.folders.slice(0, 6).map((folder) => (
              <NavButton
                key={folder.id}
                active={route.name === "folder" && route.folderId === folder.id}
                label={folder.name} onClick={() => navigate({ name: "folder", folderId: folder.id })}
              >
                <KnowledgeIcon name="folder" /><span className="knowledge-nav-label">{folder.name}</span>
              </NavButton>
            ))}
            {catalog.value.tags.slice(0, 6).map((tag) => (
              <NavButton
                key={tag.id}
                active={route.name === "tag" && route.tagId === tag.id}
                label={tag.name} onClick={() => navigate({ name: "tag", tagId: tag.id })}
              >
                <KnowledgeIcon name="tag" /><span className="knowledge-nav-label">{tag.name}</span>
              </NavButton>
            ))}
          </NavigationSection>
        ) : null}
  </>);
  const management = (
      <nav aria-label="管理导航" className="knowledge-navigation-footer">
        <NavButton
          active={route.name === "organization"}
          label="整理" onClick={() => navigate({ name: "organization" })}
        >
          <KnowledgeIcon name="organize" /><span className="knowledge-nav-label">整理</span>
        </NavButton>
        <NavButton
          active={route.name === "settings"}
          label="数据与设置" onClick={() => navigate({ name: "settings" })}
        >
          <KnowledgeIcon name="settings" /><span className="knowledge-nav-label">数据与设置</span>
        </NavButton>
      </nav>
  );
  return (
    <aside className="knowledge-sidebar knowledge-sidebar--drawer" aria-label="知识库导航">
      <div className="knowledge-sidebar__content">
        <nav aria-label="主要导航" className="knowledge-navigation-primary">
          <NavButton active={route.name === "home"} label="首页" onClick={() => navigate({ name: "home" })}>
            <KnowledgeIcon name="home" /><span className="knowledge-nav-label">首页</span>
          </NavButton>
          <NavButton
            active={route.name === "documents" || route.name === "document"}
            label="所有文档" onClick={() => navigate({ name: "documents" })}
          >
            <KnowledgeIcon name="document" /><span className="knowledge-nav-label">所有文档</span>
          </NavButton>
          <NavButton
            active={route.name === "canvases" || route.name === "canvas"}
            label="画板" onClick={() => navigate({ name: "canvases" })}
          >
            <KnowledgeIcon name="canvas" /><span className="knowledge-nav-label">画板</span>
          </NavButton>
          {favorites}
          <NavButton active={route.name === "recent"} label="最近使用" onClick={() => navigate({ name: "recent" })}>
            <KnowledgeIcon name="clock" /><span className="knowledge-nav-label">最近使用</span>
          </NavButton>
          <NavButton active={route.name === "trash"} label="回收站" onClick={() => navigate({ name: "trash" })}>
            <KnowledgeIcon name="trash" /><span className="knowledge-nav-label">回收站</span>
          </NavButton>
          <NavButton
            active={route.name === "search"}
            label="搜索" onClick={() => navigate({ name: "search", query: "" })}
          >
            <KnowledgeIcon name="search" /><span className="knowledge-nav-label">搜索</span>
          </NavButton>
        </nav>
        {sections}
      </div>
      {management}
    </aside>
  );
}

function MobileNavigation({
  route,
  navigate,
}: {
  route: KnowledgeRoute;
  navigate: (route: KnowledgeRoute) => void;
}) {
  const items: Array<{
    label: string;
    icon: KnowledgeIconName;
    active: boolean;
    route: KnowledgeRoute;
  }> = [
    { label: "首页", icon: "home", active: route.name === "home", route: { name: "home" } },
    {
      label: "文档",
      icon: "document",
      active: route.name === "documents" || route.name === "document",
      route: { name: "documents" },
    },
    {
      label: "画板",
      icon: "canvas",
      active: route.name === "canvases" || route.name === "canvas",
      route: { name: "canvases" },
    },
    {
      label: "整理",
      icon: "organize",
      active:
        route.name === "organization" ||
        route.name === "folder" ||
        route.name === "tag" ||
        route.name === "favorites" ||
        route.name === "trash",
      route: { name: "organization" },
    },
    {
      label: "设置",
      icon: "settings",
      active: route.name === "settings",
      route: { name: "settings" },
    },
  ];
  return (
    <nav className="knowledge-mobile-nav" aria-label="移动导航">
      {items.map((item) => (
        <button
          key={item.label}
          type="button"
          className={item.active ? "is-active" : ""}
          onClick={() => navigate(item.route)}
          aria-current={item.active ? "page" : undefined}
        >
          <span aria-hidden="true"><KnowledgeIcon name={item.icon} /></span>
          <small>{item.label}</small>
        </button>
      ))}
    </nav>
  );
}

function RouteContent({
  route,
  canvasHeaderTarget,
  viewModel,
  navigate,
  routeLeaveRegistry,
}: {
  route: KnowledgeRoute;
  canvasHeaderTarget: HTMLElement | null;
  viewModel: KnowledgeAppViewModel;
  navigate: (route: KnowledgeRoute) => void;
  routeLeaveRegistry: KnowledgeRouteLeaveRegistry;
}) {
  switch (route.name) {
    case "home":
      return (
        <HomePage
          viewModel={viewModel}
          navigate={navigate}
        />
      );
    case "documents":
      return <CollectionPage kind="document" title="文档" eyebrow="写作与阅读" viewModel={viewModel} navigate={navigate} />;
    case "document":
      return (
        <RouteErrorBoundary
          resetKey={route.itemId}
          title="文档编辑器无法加载"
        >
          <Suspense fallback={<LoadingState label="加载块编辑器" lines={7} />}>
            <RichDocumentPage
              key={route.itemId}
              itemId={route.itemId}
              {...(route.blockId ? { blockId: route.blockId } : {})}
              viewModel={viewModel}
              navigate={navigate}
              routeLeaveRegistry={routeLeaveRegistry}
            />
          </Suspense>
        </RouteErrorBoundary>
      );
    case "canvases":
      return <CollectionPage kind="canvas" title="画板" eyebrow="视觉思考" viewModel={viewModel} navigate={navigate} />;
    case "canvas":
      return (
        <RouteErrorBoundary
          resetKey={route.itemId}
          title="画板编辑器无法加载"
        >
          <Suspense fallback={<LoadingState label="加载完整画板" lines={7} />}>
            <KnowledgeCanvasPage
              key={route.itemId}
              itemId={route.itemId}
              headerTarget={canvasHeaderTarget}
              viewModel={viewModel}
              navigate={navigate}
              routeLeaveRegistry={routeLeaveRegistry}
            />
          </Suspense>
        </RouteErrorBoundary>
      );
    case "recent":
      return <CollectionPage collection="recent" title="最近使用" eyebrow="按更新时间排序" viewModel={viewModel} navigate={navigate} />;
    case "favorites":
      return <CollectionPage collection="favorites" title="收藏" eyebrow="重要内容" viewModel={viewModel} navigate={navigate} />;
    case "trash":
      return <CollectionPage collection="trash" title="回收站" eyebrow="可恢复内容" viewModel={viewModel} navigate={navigate} />;
    case "folder":
      return <CatalogCollectionPage type="folder" id={route.folderId} viewModel={viewModel} navigate={navigate} />;
    case "tag":
      return <CatalogCollectionPage type="tag" id={route.tagId} viewModel={viewModel} navigate={navigate} />;
    case "organization":
      return <OrganizationPage viewModel={viewModel} navigate={navigate} />;
    case "search":
      return <SearchPage initialQuery={route.query} viewModel={viewModel} navigate={navigate} />;
    case "settings":
      return <SettingsPage viewModel={viewModel} navigate={navigate} />;
  }
}

function HomePage({
  viewModel,
  navigate,
}: {
  viewModel: KnowledgeAppViewModel;
  navigate: (route: KnowledgeRoute) => void;
}) {
  const resource = useResource(() => viewModel.list(), [viewModel]);
  return (
    <Page title="最近编辑" className="knowledge-home-page">
      <div className="knowledge-home-list-heading">
        <span>接着上次的思路，继续记录。</span>
        <button type="button" onClick={() => navigate({ name: "recent" })}>查看全部 <span aria-hidden="true">→</span></button>
      </div>
      {resource.status === "loading" ? <LoadingState label="加载本地笔记" /> : null}
      {resource.status === "error" ? (
        <ErrorState title="本地知识库暂时无法读取" details={errorMessage(resource.error)} onRetry={resource.reload} />
      ) : null}
      {resource.status === "ready" ? (
        <KnowledgeItemGrid
          items={resource.value.slice(0, 10)}
          viewMode="list"
          emptyTitle="写下第一条笔记"
          emptyDescription="点击右上角“新建”，开始记录笔记或画板。"
          onOpen={(item) =>
            navigate(
              item.kind === "document"
                ? { name: "document", itemId: item.id }
                : { name: "canvas", itemId: item.id },
            )
          }
        />
      ) : null}
    </Page>
  );
}

function CollectionPage({
  kind,
  collection = "all",
  folderId,
  tagId,
  title,
  viewModel,
  navigate,
}: {
  kind?: "document" | "canvas";
  collection?: KnowledgeCollection;
  folderId?: string;
  tagId?: string;
  title: string;
  eyebrow: string;
  viewModel: KnowledgeAppViewModel;
  navigate: (route: KnowledgeRoute) => void;
}) {
  const { showToast } = useToast();
  const filtersId = useId();
  const filterTrigger = useRef<HTMLButtonElement>(null);
  const resource = useResource(
    () =>
      viewModel.listCollection({
        collection,
        ...(kind === undefined ? {} : { kind }),
        ...(folderId === undefined ? {} : { folderId }),
        ...(tagId === undefined ? {} : { tagId }),
      }),
    [viewModel, collection, kind, folderId, tagId],
  );
  const catalog = useResource(() => viewModel.loadCatalog(), [viewModel]);
  const [creating, setCreating] = useState(false);
  const [busyItemId, setBusyItemId] = useState<string>();
  const [metadataItem, setMetadataItem] = useState<KnowledgeItem>();
  const [metadataError, setMetadataError] = useState<string>();
  const [pendingAction, setPendingAction] = useState<{
    readonly kind: "trash" | "purge";
    readonly item: KnowledgeItem;
  }>();
  const [emptyTrashOpen, setEmptyTrashOpen] = useState(false);
  const [filterQuery, setFilterQuery] = useState("");
  const [viewMode, setViewMode] = useState<CollectionViewLayout>("list");
  const [sortMode, setSortMode] = useState<CollectionViewSort>("updated-desc");
  const [folderFilter, setFolderFilter] = useState("");
  const [tagFilter, setTagFilter] = useState("");
  const [dateFilter, setDateFilter] = useState<"all" | "7" | "30">("all");
  const [groupMode, setGroupMode] = useState<CollectionViewGroup>("none");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<ReadonlySet<string>>(() => new Set());
  const [bulkFolderId, setBulkFolderId] = useState("");
  const [bulkTagId, setBulkTagId] = useState("");
  const [bulkBusy, setBulkBusy] = useState(false);
  const [savedViews, setSavedViews] = useState<readonly SavedCollectionView[]>(() => readSavedViews());
  const [savedViewId, setSavedViewId] = useState("");
  const [viewName, setViewName] = useState("");
  const normalizedFilterQuery = normalizeCollectionQuery(filterQuery);
  const filteredItems =
    resource.status === "ready"
      ? applyCollectionView(resource.value, {
          query: filterQuery,
          sort: sortMode,
          folder: folderFilter,
          tag: tagFilter,
          date: dateFilter,
          group: groupMode,
          layout: viewMode,
        })
      : [];
  const selectedItems = filteredItems.filter((item) => selectedIds.has(item.id));
  const groupedItems = groupCollectionItems(filteredItems, groupMode, catalog.status === "ready" ? catalog.value : undefined);
  const activeAdvancedFilterCount = activeCollectionViewFilterCount({
    query: filterQuery,
    sort: sortMode,
    folder: folderFilter,
    tag: tagFilter,
    date: dateFilter,
    group: groupMode,
    layout: viewMode,
  });

  const reload = () => {
    resource.reload();
    catalog.reload();
  };

  useEffect(() => {
    if (resource.status !== "ready") return;
    const valid = new Set(resource.value.map((item) => item.id));
    setSelectedIds((current) => new Set([...current].filter((id) => valid.has(id))));
  }, [resource.status === "ready" ? resource.value : undefined]);

  const runBulk = async (mutation: Parameters<KnowledgeAppViewModel["bulkUpdateItems"]>[1], successTitle: string) => {
    if (selectedItems.length === 0 || bulkBusy) return;
    setBulkBusy(true);
    try {
      const result = await viewModel.bulkUpdateItems(selectedItems, mutation);
      setSelectedIds(new Set());
      reload();
      const undoItems = mutation.kind === "trash" ? result.succeeded : [];
      showToast({
        tone: result.failures.length ? "info" : "success",
        title: result.failures.length
          ? `${result.succeeded.length} 项完成，${result.failures.length} 项失败`
          : successTitle,
        ...(undoItems.length > 0
          ? {
              durationMs: 8_000,
              action: {
                label: "撤销",
                onClick: async () => {
                  try {
                    const restored = await viewModel.bulkUpdateItems(
                      undoItems,
                      { kind: "restore" },
                    );
                    reload();
                    showToast({
                      tone: restored.failures.length ? "info" : "success",
                      title: restored.failures.length
                        ? `已恢复 ${restored.succeeded.length} 项，${restored.failures.length} 项失败`
                        : `已恢复 ${restored.succeeded.length} 项`,
                    });
                  } catch (cause) {
                    showToast({
                      tone: "error",
                      title: "撤销失败",
                      description: errorMessage(cause),
                    });
                  }
                },
              },
            }
          : {}),
      });
    } catch (cause) {
      showToast({ tone: "error", title: "批量操作失败", description: errorMessage(cause) });
    } finally { setBulkBusy(false); }
  };

  const runItemAction = async (
    item: KnowledgeItem,
    operation: () => Promise<unknown>,
    successTitle: string,
  ): Promise<boolean> => {
    if (busyItemId) return false;
    setBusyItemId(item.id);
    try {
      await operation();
      reload();
      showToast({ tone: "success", title: successTitle });
      return true;
    } catch (cause) {
      showToast({
        tone: "error",
        title: "操作失败",
        description: errorMessage(cause),
      });
      return false;
    } finally {
      setBusyItemId(undefined);
    }
  };

  const resetCollectionView = () => {
    setFilterQuery("");
    setSortMode("updated-desc");
    setFolderFilter("");
    setTagFilter("");
    setDateFilter("all");
    setGroupMode("none");
    setSavedViewId("");
  };

  const applySavedView = (saved: SavedCollectionView) => {
    setFilterQuery(saved.query);
    setSortMode(saved.sort);
    setFolderFilter(saved.folder);
    setTagFilter(saved.tag);
    setDateFilter(saved.date);
    setGroupMode(saved.group);
    setViewMode(saved.layout ?? "list");
  };

  const currentSavedView = (id: string, name: string): SavedCollectionView => ({
    id,
    name,
    query: filterQuery,
    sort: sortMode,
    folder: folderFilter,
    tag: tagFilter,
    date: dateFilter,
    group: groupMode,
    layout: viewMode,
  });

  const persistSavedViews = (
    next: readonly SavedCollectionView[],
    successTitle: string,
  ) => {
    setSavedViews(next);
    if (writeSavedViews(next)) {
      showToast({ tone: "success", title: successTitle });
    } else {
      showToast({
        tone: "info",
        title: "视图仅在本次打开期间可用",
        description: "浏览器阻止了本机偏好存储。",
      });
    }
  };

  const saveNewView = () => {
    const name = viewName.trim();
    if (!name) return;
    if (savedViews.length >= MAX_SAVED_VIEWS) {
      showToast({ tone: "info", title: `最多保存 ${MAX_SAVED_VIEWS} 个视图` });
      return;
    }
    if (savedViews.some((view) => view.name.localeCompare(name, undefined, { sensitivity: "accent" }) === 0)) {
      showToast({ tone: "info", title: "已有同名视图", description: "可以选择该视图后更新，或换一个名称。" });
      return;
    }
    const created = currentSavedView(crypto.randomUUID(), name);
    persistSavedViews([...savedViews, created], "已保存当前视图");
    setSavedViewId(created.id);
    setViewName("");
  };

  const updateSelectedView = () => {
    const selected = savedViews.find((view) => view.id === savedViewId);
    if (!selected) return;
    persistSavedViews(
      savedViews.map((view) =>
        view.id === selected.id
          ? currentSavedView(view.id, view.name)
          : view,
      ),
      `已更新“${selected.name}”`,
    );
  };

  const deleteSelectedView = () => {
    const selected = savedViews.find((view) => view.id === savedViewId);
    if (!selected) return;
    persistSavedViews(
      savedViews.filter((view) => view.id !== selected.id),
      `已删除“${selected.name}”`,
    );
    setSavedViewId("");
  };

  return (
    <Page
      title={title}
      className="knowledge-collection-page"
      action={
        collection === "trash" ? (
          <Button
            variant="danger"
            disabled={resource.status !== "ready" || resource.value.length === 0}
            onClick={() => setEmptyTrashOpen(true)}
          >
            清空回收站
          </Button>
        ) : kind && !folderId && !tagId ? (
          <Button
            variant="primary"
            disabled={creating}
            onClick={async () => {
              setCreating(true);
              try {
                const item =
                  kind === "canvas"
                    ? await viewModel.createCanvas()
                    : await viewModel.createDocument();
                navigate(
                  kind === "canvas"
                    ? { name: "canvas", itemId: item.id }
                    : { name: "document", itemId: item.id },
                );
              } catch (cause) {
                showToast({
                  tone: "error",
                  title: kind === "canvas" ? "无法新建画板" : "无法新建文档",
                  description: errorMessage(cause),
                });
              } finally {
                setCreating(false);
              }
            }}
          >
            <KnowledgeIcon name="plus" />
            {creating
              ? "创建中…"
              : kind === "canvas"
                ? "新建画板"
                : "新建文档"}
          </Button>
        ) : null
      }
    >
      {resource.status === "loading" ? <LoadingState /> : null}
      {resource.status === "error" ? (
        <ErrorState title="无法加载列表" details={errorMessage(resource.error)} onRetry={resource.reload} />
      ) : null}
      {resource.status === "ready" ? (
        <>
          <div className="knowledge-collection-toolbar">
            <label className="knowledge-collection-filter">
              <KnowledgeIcon name="search" />
              <span className="ui-visually-hidden">筛选当前列表</span>
              <input
                type="search"
                value={filterQuery}
                placeholder="筛选当前列表"
                onChange={(event) => setFilterQuery(event.currentTarget.value)}
              />
            </label>
            <span className="knowledge-collection-count">
              {normalizedFilterQuery
                ? `${filteredItems.length.toLocaleString()} / ${resource.value.length.toLocaleString()} 项`
                : `${resource.value.length.toLocaleString()} 项`}
            </span>
            <div className="knowledge-collection-toolbar__actions">
              <Button ref={filterTrigger} size="small" variant="ghost" aria-label="筛选与视图"
                aria-description={`${activeAdvancedFilterCount} 项条件已启用`} aria-expanded={filtersOpen} aria-controls={filtersId}
                onClick={() => setFiltersOpen(!filtersOpen)}>
                <KnowledgeIcon name="organize" />筛选与视图
                {activeAdvancedFilterCount > 0 ? <span className="knowledge-progressive-panel__badge">{activeAdvancedFilterCount}</span> : null}
              </Button>
              <div className="knowledge-view-toggle" aria-label="内容排列方式">
                <button
                  type="button"
                  aria-label="列表视图"
                  aria-pressed={viewMode === "list"}
                  onClick={() => setViewMode("list")}
                >
                  <KnowledgeIcon name="list" />
                </button>
                <button
                  type="button"
                  aria-label="网格视图"
                  aria-pressed={viewMode === "grid"}
                  onClick={() => setViewMode("grid")}
                >
                  <KnowledgeIcon name="grid" />
                </button>
              </div>
              {collection !== "trash" ? (
                <Button
                  size="small"
                  variant={selectionMode ? "secondary" : "ghost"}
                  aria-pressed={selectionMode}
                  onClick={() => {
                    setSelectionMode((current) => {
                      if (current) setSelectedIds(new Set());
                      return !current;
                    });
                  }}
                >
                  {selectionMode ? "完成选择" : "批量整理"}
                </Button>
              ) : null}
            </div>
          </div>
          {filtersOpen ? <section id={filtersId} className="knowledge-collection-options" aria-label="筛选与视图"
            onKeyDown={(event) => { if (event.key === "Escape") { event.preventDefault(); setFiltersOpen(false); filterTrigger.current?.focus(); } }}>
            <div className="knowledge-collection-filters" aria-label="筛选、排序与分组">
              <label><span>排序</span><select value={sortMode} onChange={(event) => setSortMode(event.currentTarget.value as CollectionViewSort)}><option value="updated-desc">最近更新</option><option value="updated-asc">最早更新</option><option value="created-desc">最近创建</option><option value="title-asc">标题 A–Z</option></select></label>
              <label><span>文件夹</span><select value={folderFilter} onChange={(event) => setFolderFilter(event.currentTarget.value)}><option value="">全部文件夹</option><option value="__none">未归档</option>{catalog.status === "ready" ? catalog.value.folders.map((folder) => <option key={folder.id} value={folder.id}>{folder.name}</option>) : null}</select></label>
              <label><span>标签</span><select value={tagFilter} onChange={(event) => setTagFilter(event.currentTarget.value)}><option value="">全部标签</option>{catalog.status === "ready" ? catalog.value.tags.map((tag) => <option key={tag.id} value={tag.id}>{tag.name}</option>) : null}</select></label>
              <label><span>更新时间</span><select value={dateFilter} onChange={(event) => setDateFilter(event.currentTarget.value as typeof dateFilter)}><option value="all">不限</option><option value="7">近 7 天</option><option value="30">近 30 天</option></select></label>
              <label><span>分组</span><select value={groupMode} onChange={(event) => setGroupMode(event.currentTarget.value as CollectionViewGroup)}><option value="none">不分组</option><option value="folder">按文件夹</option><option value="status">按状态</option><option value="kind">按类型</option></select></label>
            </div>
            <div className="knowledge-saved-views">
              <Button size="small" variant="ghost" disabled={!normalizedFilterQuery && activeAdvancedFilterCount === 0 && !savedViewId} onClick={resetCollectionView}>清除全部条件</Button>
              <label><span className="ui-visually-hidden">保存的视图</span><select value={savedViewId} onChange={(event) => { const id = event.currentTarget.value; setSavedViewId(id); const saved = savedViews.find((entry) => entry.id === id); if (saved) applySavedView(saved); }}><option value="">应用保存的视图…</option>{savedViews.map((view) => <option key={view.id} value={view.id}>{view.name}</option>)}</select></label>
              <input value={viewName} maxLength={40} placeholder="新视图名称" aria-label="新视图名称" onChange={(event) => setViewName(event.currentTarget.value)} />
              <Button size="small" variant="ghost" disabled={!viewName.trim()} onClick={saveNewView}>另存为视图</Button>
              <Button size="small" variant="ghost" disabled={!savedViewId} onClick={updateSelectedView}>更新所选视图</Button>
              <Button size="small" variant="ghost" disabled={!savedViewId} onClick={deleteSelectedView}>删除所选视图</Button>
            </div>
          </section> : null}
          {collection !== "trash" && selectionMode ? (
            <div className={`knowledge-bulk-toolbar${selectedItems.length ? " is-active" : ""}`} role="toolbar" aria-label="批量整理">
              <label><input type="checkbox" checked={filteredItems.length > 0 && filteredItems.every((item) => selectedIds.has(item.id))} onChange={(event) => setSelectedIds(event.currentTarget.checked ? new Set(filteredItems.map((item) => item.id)) : new Set())} /> 选择当前 {filteredItems.length} 项</label>
              <strong>{selectedItems.length} 项已选择</strong>
              <select aria-label="批量移动到文件夹" value={bulkFolderId} onChange={(event) => setBulkFolderId(event.currentTarget.value)}><option value="">无文件夹</option>{catalog.status === "ready" ? catalog.value.folders.map((folder) => <option key={folder.id} value={folder.id}>{folder.name}</option>) : null}</select>
              <Button size="small" disabled={!selectedItems.length || bulkBusy} onClick={() => void runBulk({ kind: "move", folderId: bulkFolderId || null }, "已批量移动")}>移动</Button>
              <select aria-label="批量添加标签" value={bulkTagId} onChange={(event) => setBulkTagId(event.currentTarget.value)}><option value="">选择标签</option>{catalog.status === "ready" ? catalog.value.tags.map((tag) => <option key={tag.id} value={tag.id}>{tag.name}</option>) : null}</select>
              <Button size="small" disabled={!selectedItems.length || !bulkTagId || bulkBusy} onClick={() => void runBulk({ kind: "tags", tagIds: [bulkTagId], mode: "add" }, "已批量添加标签")}>加标签</Button>
              <Button size="small" variant="ghost" disabled={!selectedItems.length || bulkBusy} onClick={() => void runBulk({ kind: "favorite", favorite: true }, "已批量收藏")}>收藏</Button>
              <Button size="small" variant="ghost" disabled={!selectedItems.length || bulkBusy} onClick={async () => { setBulkBusy(true); try { await viewModel.exportItems(selectedItems); showToast({ tone: "success", title: `已导出 ${selectedItems.length} 项` }); } catch (cause) { showToast({ tone: "error", title: "批量导出失败", description: errorMessage(cause) }); } finally { setBulkBusy(false); } }}>导出</Button>
              <Button size="small" variant="danger" disabled={!selectedItems.length || bulkBusy} onClick={() => void runBulk({ kind: "trash" }, "已移到回收站")}>移到回收站</Button>
            </div>
          ) : null}
          {groupedItems.length === 0 ? (
            <EmptyState
              compact
              title={normalizedFilterQuery || activeAdvancedFilterCount > 0 ? "没有匹配当前筛选的内容" : collection === "trash" ? "回收站为空" : `${title}中暂无内容`}
              description={normalizedFilterQuery || activeAdvancedFilterCount > 0 ? "可以缩短关键词或调整筛选条件。" : "新内容会保存在本机知识库中。"}
            />
          ) : groupedItems.map(([groupTitle, groupItems]) => (
            <section className="knowledge-collection-group" key={groupTitle}>
              {groupMode !== "none" ? <header><strong>{groupTitle}</strong><span>{groupItems.length} 项</span></header> : null}
              <KnowledgeItemGrid
                items={groupItems}
                viewMode={viewMode}
                emptyTitle={filterQuery ? "没有匹配当前筛选的内容" : collection === "trash" ? "回收站为空" : `${title}中暂无内容`}
                {...(collection === "trash" ? {} : { ...(selectionMode ? { selectedIds, onSelectionChange: (item: KnowledgeItem, selected: boolean) => setSelectedIds((current) => { const next = new Set(current); if (selected) next.add(item.id); else next.delete(item.id); return next; }) } : {}), onOpen: (item: KnowledgeItem) => navigate(item.kind === "document" ? { name: "document", itemId: item.id } : { name: "canvas", itemId: item.id }) })}
                {...(busyItemId === undefined ? {} : { busyItemId })}
                {...(collection === "trash" ? { onRestore: (item: KnowledgeItem) => void runItemAction(item, () => viewModel.restoreItem(item), "内容已恢复"), onPurge: (item: KnowledgeItem) => setPendingAction({ kind: "purge", item }) } : { onManage: (item: KnowledgeItem) => { setMetadataError(undefined); setMetadataItem(item); }, onFavorite: (item: KnowledgeItem, favorite: boolean) => void runItemAction(item, () => viewModel.setFavorite(item, favorite), favorite ? "已加入收藏" : "已取消收藏"), onTrash: (item: KnowledgeItem) => setPendingAction({ kind: "trash", item }) })}
              />
            </section>
          ))}
        </>
      ) : null}
      <ItemMetadataDialog
        item={metadataItem}
        {...(catalog.status === "ready" ? { catalog: catalog.value } : {})}
        saving={busyItemId === metadataItem?.id}
        {...(metadataError === undefined ? {} : { error: metadataError })}
        onOpenChange={(open) => {
          if (!open) setMetadataItem(undefined);
        }}
        onSave={async (input) => {
          if (!metadataItem) return;
          setMetadataError(undefined);
          const saved = await runItemAction(
            metadataItem,
            () => viewModel.updateItemMetadata(metadataItem, input),
            "整理信息已保存",
          );
          if (saved) {
            setMetadataItem(undefined);
          } else {
            setMetadataError("保存失败，请确认内容后重试。");
          }
        }}
      />
      <ConfirmDialog
        open={pendingAction !== undefined}
        onOpenChange={(open) => {
          if (!open) setPendingAction(undefined);
        }}
        title={
          pendingAction?.kind === "purge"
            ? `永久删除“${pendingAction.item.title}”？`
            : `将“${pendingAction?.item.title ?? "这条内容"}”移到回收站？`
        }
        description={
          pendingAction?.kind === "purge"
            ? "正文、画板、版本历史和相关附件引用将被永久清除，此操作无法撤销。"
            : "内容会保留在回收站，可以稍后恢复。"
        }
        confirmLabel={pendingAction?.kind === "purge" ? "永久删除" : "移到回收站"}
        cancelLabel="取消"
        submittingLabel="处理中…"
        errorLabel="操作失败。"
        destructive
        onConfirm={async () => {
          if (!pendingAction) return;
          const { item, kind: action } = pendingAction;
          const completed = await runItemAction(
            item,
            () =>
              action === "purge"
                ? viewModel.purgeItem(item)
                : viewModel.moveItemToTrash(item),
            action === "purge" ? "内容已永久删除" : "内容已移到回收站",
          );
          if (!completed) throw new Error("操作未完成，请稍后重试。");
          setPendingAction(undefined);
        }}
      />
      <ConfirmDialog
        open={emptyTrashOpen}
        onOpenChange={setEmptyTrashOpen}
        title="清空回收站？"
        description="回收站中的所有正文、画板、版本历史和相关附件引用都会被永久清除。"
        confirmLabel="永久清空"
        cancelLabel="取消"
        submittingLabel="正在清空…"
        errorLabel="清空失败。"
        destructive
        onConfirm={async () => {
          const count = await viewModel.emptyTrash();
          reload();
          showToast({ tone: "success", title: `已永久删除 ${count} 条内容` });
        }}
      />
    </Page>
  );
}

function CatalogCollectionPage({
  type,
  id,
  viewModel,
  navigate,
}: {
  type: "folder" | "tag";
  id: string;
  viewModel: KnowledgeAppViewModel;
  navigate: (route: KnowledgeRoute) => void;
}) {
  const catalog = useResource(() => viewModel.loadCatalog(), [viewModel, id]);
  if (catalog.status === "loading") return <LoadingState label="读取分类" />;
  if (catalog.status === "error") {
    return <ErrorState title="无法读取分类" details={errorMessage(catalog.error)} onRetry={catalog.reload} />;
  }
  const entry =
    type === "folder"
      ? catalog.value.folders.find((folder) => folder.id === id)
      : catalog.value.tags.find((tag) => tag.id === id);
  if (!entry) {
    return <EmptyState title="分类不存在" description="它可能已被删除或由另一版本创建。" />;
  }
  return (
    <CollectionPage
      title={entry.name}
      eyebrow={type === "folder" ? "文件夹" : "标签"}
      {...(type === "folder" ? { folderId: id } : { tagId: id })}
      viewModel={viewModel}
      navigate={navigate}
    />
  );
}

function OrganizationPage({
  viewModel,
  navigate,
}: {
  viewModel: KnowledgeAppViewModel;
  navigate: (route: KnowledgeRoute) => void;
}) {
  const catalog = useResource(() => viewModel.loadCatalog(), [viewModel]);
  return (
    <Page title="整理知识库" eyebrow="文件夹与标签">
      {catalog.status === "loading" ? <LoadingState label="读取整理信息" /> : null}
      {catalog.status === "error" ? (
        <ErrorState title="无法读取整理信息" details={errorMessage(catalog.error)} onRetry={catalog.reload} />
      ) : null}
      {catalog.status === "ready" ? (
        <CatalogManager
          catalog={catalog.value}
          viewModel={viewModel}
          onChanged={catalog.reload}
          onOpenFolder={(folderId) => navigate({ name: "folder", folderId })}
          onOpenTag={(tagId) => navigate({ name: "tag", tagId })}
          onOpenFavorites={() => navigate({ name: "favorites" })}
          onOpenTrash={() => navigate({ name: "trash" })}
        />
      ) : null}
    </Page>
  );
}

function SearchPage({
  initialQuery,
  viewModel,
  navigate,
}: {
  initialQuery: string;
  viewModel: KnowledgeAppViewModel;
  navigate: (route: KnowledgeRoute) => void;
}) {
  const [query, setQuery] = useState(initialQuery);
  const [submitted, setSubmitted] = useState(initialQuery);
  const resource = useResource(() => viewModel.searchDetailed(submitted), [viewModel, submitted]);

  useEffect(() => {
    setQuery(initialQuery);
    setSubmitted(initialQuery);
  }, [initialQuery]);

  return (
    <Page title="搜索" eyebrow="本机内容">
      <form
        className="knowledge-search-form"
        onSubmit={(event) => {
          event.preventDefault();
          setSubmitted(query);
          navigate({ name: "search", query });
        }}
      >
        <TextField
          label="关键词"
          value={query}
          inputMode="search"
          enterKeyHint="search"
          onChange={(event) => setQuery(event.currentTarget.value)}
        />
        <Button variant="primary" type="submit">搜索</Button>
      </form>
      {resource.status === "loading" ? <LoadingState compact /> : null}
      {resource.status === "error" ? <ErrorState title="搜索失败" details={errorMessage(resource.error)} /> : null}
      {resource.status === "ready" ? (
        resource.value.length ? <div className="knowledge-search-results">{resource.value.map((hit, index) => (
          <button key={`${hit.item.id}:${hit.blockId ?? "title"}:${index}`} type="button" onClick={() => navigate(hit.item.kind === "document" ? { name: "document", itemId: hit.item.id, ...(hit.blockId ? { blockId: hit.blockId } : {}) } : { name: "canvas", itemId: hit.item.id })}>
            <span aria-hidden="true">{hit.item.kind === "document" ? "▤" : "◇"}</span>
            <span><strong>{hit.item.title || "无标题"}</strong><small>{hit.locationLabel}</small><p>{highlightSnippet(hit.snippet, submitted)}</p></span>
            <span aria-hidden="true">→</span>
          </button>
        ))}</div> : <EmptyState compact title={submitted ? "没有匹配内容" : "输入关键词开始搜索"} description="搜索结果会精确定位到命中的内容块。" />
      ) : null}
    </Page>
  );
}

function SettingsPage({ viewModel, navigate }: {
  viewModel: KnowledgeAppViewModel;
  navigate: (route: KnowledgeRoute) => void;
}) {
  const { showToast } = useToast();
  const runtime = viewModel.runtime;
  const health = useResource(() => runtime.storageHealth.inspect(), [runtime]);
  const fileInput = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [pendingImport, setPendingImport] = useState<File>();
  const [resetOpen, setResetOpen] = useState(false);
  const [lastBackup, setLastBackup] = useState<string>();

  const run = async (label: string, work: () => Promise<void>) => {
    setBusy(label);
    try {
      await work();
      showToast({ tone: "success", title: `${label}完成` });
    } catch (error) {
      showToast({
        tone: "error",
        title: `${label}失败`,
        description: errorMessage(error),
      });
    } finally {
      setBusy(null);
    }
  };

  return (
    <Page title="设置">
      <p className="knowledge-lede">
        管理内容分类、回收站与本机数据。
      </p>
      <nav className="knowledge-settings-navigation" aria-label="内容管理">
        <button type="button" onClick={() => navigate({ name: "organization" })}>
          <KnowledgeIcon name="organize" />
          <span><strong>整理与收藏</strong><small>管理文件夹、标签与收藏的内容</small></span>
          <span aria-hidden="true">→</span>
        </button>
        <button type="button" onClick={() => navigate({ name: "trash" })}>
          <KnowledgeIcon name="trash" />
          <span><strong>回收站</strong><small>查看和恢复已删除的内容</small></span>
          <span aria-hidden="true">→</span>
        </button>
      </nav>
      <section className="knowledge-settings-card knowledge-storage-card">
        <div className="knowledge-settings-card__heading">
          <span className="knowledge-settings-card__icon"><KnowledgeIcon name="shield" /></span>
          <div>
            <h2>本机知识库</h2>
            <p>笔记正文、画板和附件索引优先保存在当前设备。</p>
          </div>
          <span
            className="knowledge-health-badge"
            data-status={health.status === "ready" ? "healthy" : health.status}
          >
            <span aria-hidden="true" />
            {health.status === "ready"
              ? "正常"
              : health.status === "error"
                ? "异常"
                : "检查中"}
          </span>
        </div>
        {health.status === "loading" ? <LoadingState compact /> : null}
        {health.status === "error" ? (
          <ErrorState
            title="无法检查本机存储"
            details={errorMessage(health.error)}
            onRetry={health.reload}
          />
        ) : null}
        {health.status === "ready" ? (
          <StorageHealthSummary health={health.value} />
        ) : null}
      </section>

      <section className="knowledge-settings-card">
        <div className="knowledge-settings-card__heading">
          <span className="knowledge-settings-card__icon"><KnowledgeIcon name="backup" /></span>
          <div>
            <h2>完整备份</h2>
            <p>
              导出带校验信息的便携 .notebundle，可在 Web 与移动端之间恢复；桌面本机快照使用独立格式。
            </p>
          </div>
        </div>
        <div className="knowledge-settings-actions">
          <Button
            variant="primary"
            leadingIcon={<KnowledgeIcon name="download" />}
            disabled={busy !== null}
            onClick={() =>
              void run("导出", async () => {
                const bundle = await runtime.bundles.exportBundle();
                await runtime.bundles.downloadBundle(bundle);
                setLastBackup(bundle.fileName);
              })
            }
          >
            立即完整备份
          </Button>
          <Button
            leadingIcon={<KnowledgeIcon name="upload" />}
            disabled={busy !== null}
            onClick={() => fileInput.current?.click()}
          >
            从备份恢复
          </Button>
          <input
            ref={fileInput}
            hidden
            type="file"
            accept=".notebundle,application/zip"
            onChange={(event) => {
              const file = event.currentTarget.files?.[0];
              event.currentTarget.value = "";
              if (!file) return;
              setPendingImport(file);
            }}
          />
        </div>
        {lastBackup ? (
          <p className="knowledge-backup-success" role="status">
            <KnowledgeIcon name="check" /> 已完成备份：{lastBackup}
          </p>
        ) : null}
      </section>

      <section className="knowledge-settings-card knowledge-settings-list">
        <div className="knowledge-setting-row">
          <span className="knowledge-settings-card__icon"><KnowledgeIcon name="shield" /></span>
          <div><strong>浏览器持久存储</strong><small>降低 Web 数据被浏览器自动回收的风险</small></div>
          <Button
            size="small"
            disabled={busy !== null || health.status !== "ready" || health.value.persisted}
            onClick={() =>
              void run("持久化请求", async () => {
                await runtime.storageHealth.requestPersistence();
                health.reload();
              })
            }
          >
            {health.status === "ready" && health.value.persisted ? "已开启" : "重新请求"}
          </Button>
        </div>
        <div className="knowledge-setting-row knowledge-setting-row--danger">
          <span className="knowledge-settings-card__icon"><KnowledgeIcon name="trash" /></span>
          <div><strong>重置本机数据</strong><small>永久清除当前设备上的知识库，此操作无法撤销</small></div>
          <Button variant="danger" size="small" disabled={busy !== null} onClick={() => setResetOpen(true)}>
            重置
          </Button>
        </div>
      </section>

      <CapabilityMatrix runtime={runtime} />
      <ConfirmDialog
        open={pendingImport !== undefined}
        onOpenChange={(open) => {
          if (!open) setPendingImport(undefined);
        }}
        title="从这个备份恢复？"
        description="应用会先下载当前知识库的安全备份，再验证并恢复所选文件。恢复将替换本机现有数据，完成后应用会重新载入。"
        confirmLabel="先备份，再恢复"
        cancelLabel="取消"
        submittingLabel="正在备份并恢复…"
        errorLabel="恢复失败。"
        onConfirm={async () => {
          if (!pendingImport) return;
          const safetyBackup = await runtime.bundles.exportBundle();
          await runtime.bundles.downloadBundle(safetyBackup);
          setLastBackup(safetyBackup.fileName);
          await runtime.bundles.importBundle(pendingImport);
          location.reload();
        }}
      />
      <TypedConfirmDialog
        open={resetOpen}
        onOpenChange={setResetOpen}
        title="永久清除本机知识库？"
        description="应用会先下载一份安全备份，再删除所有笔记、画板和本机版本历史。此操作无法撤销。"
        confirmationText="RESET"
        inputLabel="请输入 RESET 以确认永久清除"
        confirmLabel="永久清除"
        cancelLabel="取消"
        submittingLabel="正在清除…"
        errorLabel="清除失败。"
        destructive
        onConfirm={async () => {
          const resettable = runtime.unitOfWork as { reset?: () => Promise<void> };
          if (!resettable.reset) throw new Error("当前平台不支持应用内重置。");
          const safetyBackup = await runtime.bundles.exportBundle();
          await runtime.bundles.downloadBundle(safetyBackup);
          setLastBackup(safetyBackup.fileName);
          await resettable.reset();
          location.hash = "#/";
          location.reload();
        }}
      />
    </Page>
  );
}

function CapabilityMatrix({ runtime }: { runtime: PlatformRuntime }) {
  const entries = Object.entries(runtime.capabilities).filter(([key]) => key !== "platform");
  return (
    <section className="knowledge-settings-card">
      <h2>平台能力</h2>
      <div className="knowledge-capability-list">
        {entries.map(([name, descriptor]) => {
          const capability = descriptor as { support: string };
          return (
            <div key={name}>
              <span>{capabilityLabel(name)}</span>
              <strong data-support={capability.support}>{supportLabel(capability.support)}</strong>
              <small>{capabilityDescription(name, capability.support)}</small>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function CaptureSheet({
  viewModel,
  onClose,
  onCreated,
}: {
  viewModel: KnowledgeAppViewModel;
  onClose: () => void;
  onCreated: (item: KnowledgeItem) => void;
}) {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string>();
  const [mode, setMode] = useState<"note" | "task" | "link">("note");
  const [capture, setCapture] = useState<QuickCaptureValue>({
    title: "",
    body: "",
  });
  const modeOptions = [
    { id: "note" as const, label: "笔记", icon: "document" as const },
    { id: "task" as const, label: "任务", icon: "check" as const },
    { id: "link" as const, label: "链接", icon: "archive" as const },
  ];
  const placeholders = {
    note: "记下此刻的想法…",
    task: "写下需要完成的事情…",
    link: "粘贴链接，并补充为什么值得保存…",
  };
  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open && !submitting) onClose();
      }}
      title="快速记录"
      description="直接写入本机知识库，稍后再慢慢整理。"
      closeLabel="关闭快速记录"
      className="knowledge-capture-dialog"
    >
        <CaptureModeSelector
          value={mode}
          label="记录类型"
          disabled={submitting}
          options={modeOptions.map((option) => ({
            ...option,
            icon: <KnowledgeIcon name={option.icon} />,
          }))}
          onValueChange={(value) => {
            setMode(value);
            setError(undefined);
          }}
        />
        <QuickCapture
          value={capture}
          onValueChange={setCapture}
          submitting={submitting}
          {...(error === undefined ? {} : { error })}
          onCancel={onClose}
          showTitle={false}
          bodyLabel="记录内容"
          bodyPlaceholder={placeholders[mode]}
          cancelLabel="取消"
          savingLabel="正在保存…"
          submitLabel="保存到本机"
          onSubmit={async (value) => {
            if (
              mode === "link" &&
              extractQuickCaptureWebLink(value.body) === undefined
            ) {
              setError("请粘贴一个以 http:// 或 https:// 开头的链接。");
              return;
            }
            setSubmitting(true);
            setError(undefined);
            try {
              const item = await viewModel.quickCapture({
                title: value.title,
                body: value.body,
                mode,
              });
              onCreated(item);
            } catch (cause) {
              setError(errorMessage(cause));
            } finally {
              setSubmitting(false);
            }
          }}
        />
    </Dialog>
  );
}

function NoticeStack({ notices }: { notices: readonly KnowledgeAppNotice[] }) {
  return (
    <div
      className="knowledge-notices"
      role="status"
      aria-label="应用状态"
      aria-live="polite"
    >
      {notices.map((notice) => (
        <section key={notice.id} data-tone={notice.tone} data-notice-id={notice.id}>
          <div>
            <strong>{notice.title}</strong>
            {notice.description ? <p>{notice.description}</p> : null}
          </div>
          {notice.actionLabel && notice.onAction ? (
            <Button size="small" onClick={() => void notice.onAction?.()}>
              {notice.actionLabel}
            </Button>
          ) : null}
        </section>
      ))}
    </div>
  );
}

function Page({
  className = "",
  title,
  eyebrow,
  action,
  children,
}: {
  title: string;
  className?: string;
  eyebrow?: string;
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <article className={`knowledge-page ${className}`}>
      <header className="knowledge-page__header">
        <div>
          {eyebrow ? <span>{eyebrow}</span> : null}
          <h1>{title}</h1>
        </div>
        {action}
      </header>
      {children}
    </article>
  );
}

function NavButton({
  label,
  active,
  children,
  onClick,
}: {
  label: string;
  active: boolean;
  children: ReactNode;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      className={active ? "is-active" : ""}
      onClick={onClick}
      aria-current={active ? "page" : undefined}
      aria-label={label}
      title={label}
    >
      {children}
    </button>
  );
}

function StorageHealthSummary({ health }: { health: StorageHealth }) {
  return (
    <div className="knowledge-storage-health" data-pressure={health.pressure}>
      <strong>{health.persisted ? "已获持久存储" : "可能被系统回收"}</strong>
      <span>
        {health.usage === undefined || health.quota === undefined
          ? "浏览器未公开配额"
          : `${formatBytes(health.usage)} / ${formatBytes(health.quota)}`}
      </span>
      {!health.persisted ? (
        <p>空间紧张或长期未使用时，浏览器可能清理数据。请定期导出 .notebundle。</p>
      ) : null}
    </div>
  );
}

type AsyncResource<T> =
  | { status: "loading"; reload: () => void }
  | { status: "error"; error: unknown; reload: () => void }
  | { status: "ready"; value: T; reload: () => void };

function useResource<T>(
  load: () => Promise<T>,
  dependencies: readonly unknown[],
): AsyncResource<T> {
  const [generation, setGeneration] = useState(0);
  const [state, setState] = useState<
    | { status: "loading" }
    | { status: "error"; error: unknown }
    | { status: "ready"; value: T }
  >({ status: "loading" });
  useEffect(() => {
    let active = true;
    setState({ status: "loading" });
    void load().then(
      (value) => {
        if (active) setState({ status: "ready", value });
      },
      (error: unknown) => {
        if (active) setState({ status: "error", error });
      },
    );
    return () => {
      active = false;
    };
    // The caller owns the dependency list, matching React's built-in hooks.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...dependencies, generation]);
  const reload = useCallback(() => setGeneration((value) => value + 1), []);
  return { ...state, reload } as AsyncResource<T>;
}

function capabilityLabel(name: string): string {
  return {
    structuredStorage: "结构化存储",
    attachmentStorage: "附件存储",
    files: "文件导入导出",
    camera: "相机",
    share: "系统分享",
    installable: "安装",
    backgroundSync: "云同步",
    complexCanvasEditing: "复杂画板编辑",
  }[name] ?? name;
}

function supportLabel(support: string): string {
  return support === "available" ? "可用" : support === "degraded" ? "有限" : "不可用";
}

function capabilityDescription(name: string, support: string): string {
  const available = support === "available";
  const descriptions: Readonly<Record<string, string>> = {
    structuredStorage: available ? "本机数据库可正常读写" : "当前数据格式仅支持只读访问",
    attachmentStorage: available ? "附件使用本机文件存储" : "附件使用受配额保护的本机数据库",
    files: available ? "支持导入、导出与完整备份" : "使用当前平台提供的文件选择方式",
    camera: available ? "可直接拍摄并插入附件" : "当前环境不能直接调用原生相机",
    share: available ? "可调用系统分享面板" : "仍可通过复制或导出分享",
    installable: available ? "可以安装到当前设备" : "当前应用无需再次安装",
    backgroundSync: available ? "可在后台同步变更" : "尚未配置账号与云同步传输",
    complexCanvasEditing: available ? "支持触控、连线、引用与自动保存" : "当前平台仅提供基础画板操作",
  };
  return descriptions[name] ?? (available ? "此能力可正常使用" : "此能力在当前平台受限");
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 ** 2) return `${(bytes / 1024).toFixed(1)} KiB`;
  if (bytes < 1024 ** 3) return `${(bytes / 1024 ** 2).toFixed(1)} MiB`;
  return `${(bytes / 1024 ** 3).toFixed(1)} GiB`;
}

interface SavedCollectionView {
  readonly id: string;
  readonly name: string;
  readonly query: string;
  readonly sort: CollectionViewSort;
  readonly folder: string;
  readonly tag: string;
  readonly date: "all" | "7" | "30";
  readonly group: CollectionViewGroup;
  readonly layout?: CollectionViewLayout;
}

function groupCollectionItems(items: readonly KnowledgeItem[], mode: CollectionViewGroup, catalog?: CatalogSnapshot): Array<[string, KnowledgeItem[]]> {
  const groups = groupItemsByView(items, mode);
  const folderNames = new Map(catalog?.folders.map((folder) => [folder.id, folder.name]) ?? []);
  const statusLabels: Record<string, string> = { draft: "草稿", active: "进行中", review: "待复核", done: "已完成", archived: "已归档" };
  return groups.map((group): [string, KnowledgeItem[]] => {
    const [scope, value = ""] = group.key.split(":", 2);
    const label = scope === "all"
      ? "全部"
      : scope === "folder"
        ? value === "none" ? "未归档" : folderNames.get(value) ?? "未知文件夹"
        : scope === "status"
          ? value === "none" ? "未设置状态" : statusLabels[value] ?? value
          : value === "document" ? "文档" : "画板";
    return [label, [...group.items]];
  }).sort(([left], [right]) => left.localeCompare(right, "zh-CN"));
}

const SAVED_VIEWS_KEY = "fengyue.saved-collection-views.v1";
const MAX_SAVED_VIEWS = 20;

function readSavedViews(): readonly SavedCollectionView[] {
  try {
    const value: unknown = JSON.parse(localStorage.getItem(SAVED_VIEWS_KEY) ?? "[]");
    return Array.isArray(value)
      ? value.filter(isSavedCollectionView).slice(0, MAX_SAVED_VIEWS)
      : [];
  } catch { return []; }
}

function writeSavedViews(views: readonly SavedCollectionView[]): boolean {
  try {
    localStorage.setItem(
      SAVED_VIEWS_KEY,
      JSON.stringify(views.slice(0, MAX_SAVED_VIEWS)),
    );
    return true;
  } catch {
    return false;
  }
}

function isSavedCollectionView(value: unknown): value is SavedCollectionView {
  if (!value || typeof value !== "object") return false;
  const entry = value as Record<string, unknown>;
  return (
    typeof entry["id"] === "string" && entry["id"].length > 0 &&
    typeof entry["name"] === "string" && entry["name"].trim().length > 0 &&
    typeof entry["query"] === "string" &&
    ["updated-desc", "updated-asc", "created-desc", "title-asc"].includes(String(entry["sort"])) &&
    typeof entry["folder"] === "string" &&
    typeof entry["tag"] === "string" &&
    ["all", "7", "30"].includes(String(entry["date"])) &&
    ["none", "folder", "status", "kind"].includes(String(entry["group"])) &&
    (entry["layout"] === undefined || ["grid", "list"].includes(String(entry["layout"])))
  );
}

function highlightSnippet(snippet: string, query: string): ReactNode {
  const terms = [...new Set(
    query
      .normalize("NFKC")
      .trim()
      .split(/\s+/u)
      .filter(Boolean),
  )].sort((left, right) => right.length - left.length);
  if (terms.length === 0) return snippet;
  const expression = new RegExp(
    `(${terms.map(escapeRegularExpression).join("|")})`,
    "giu",
  );
  const parts = snippet.split(expression);
  if (parts.length === 1) return snippet;
  const normalizedTerms = new Set(terms.map((term) => term.toLocaleLowerCase()));
  return <>{parts.map((part, index) =>
    normalizedTerms.has(part.toLocaleLowerCase())
      ? <mark key={`${part}:${index}`}>{part}</mark>
      : part,
  )}</>;
}

function escapeRegularExpression(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/gu, "\\$&");
}

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : "未知错误";
}

function primaryShortcut(key: string): string {
  if (typeof navigator === "undefined") return `Ctrl ${key}`;
  return /Mac|iPhone|iPad/u.test(navigator.platform)
    ? `⌘ ${key}`
    : `Ctrl ${key}`;
}
